import { ChangeEvent, SyntheticEvent } from 'react';
import { NumericFormat } from 'react-number-format';
import { UnicodeEntity } from '@mews/enums';
import { Field, Input, InputValue } from '@optimus-web/core';
import { CurrencyInputProps, CurrencySymbolProps, SymbolPosition, SymbolSpacing } from './types';

const DEFAULT_MAX_LIMIT = 999999999.99;
const DEFAULT_MIN_LIMIT = -999999999.99;

const getCurrencySymbolSpacing = (spacing: SymbolSpacing) => {
    switch (spacing) {
        case SymbolSpacing.None:
            return '';
        case SymbolSpacing.Single:
            return UnicodeEntity.Space;
    }
};

const createCurrencySymbolProps = (props: Required<CurrencySymbolProps>) => {
    switch (props.symbolPosition) {
        case SymbolPosition.Prefix:
            return {
                prefix: `${props.symbol}${getCurrencySymbolSpacing(props.symbolSpacing)}`,
            };
        case SymbolPosition.Suffix:
            return {
                suffix: `${getCurrencySymbolSpacing(props.symbolSpacing)}${props.symbol}`,
            };
    }
};

const CurrencyInputElement = ({
    size,
    value,
    onChange,
    max,
    min,
    symbol,
    symbolPosition,
    symbolSpacing,
    precision,
    decimalSeparator,
    groupSeparator,
    ...restProps
}: CurrencyInputProps) => {
    const handleChange = (event?: SyntheticEvent<HTMLInputElement>, inputValue?: InputValue) => {
        if (onChange && event) {
            onChange(event as ChangeEvent<HTMLInputElement>, inputValue ?? '');
        }
    };

    return (
        <NumericFormat
            decimalScale={precision}
            thousandSeparator={groupSeparator}
            decimalSeparator={decimalSeparator}
            customInput={Input}
            autoComplete="off"
            onValueChange={(values, sourceInfo) => {
                handleChange(sourceInfo.event, values.floatValue);
            }}
            size={size}
            isAllowed={({ floatValue }) =>
                typeof floatValue === 'number'
                    ? floatValue <= (max ?? DEFAULT_MAX_LIMIT) && floatValue >= (min ?? DEFAULT_MIN_LIMIT)
                    : true
            }
            defaultValue={value}
            value={value}
            {...(symbol && symbolPosition && symbolSpacing
                ? createCurrencySymbolProps({
                      symbol,
                      symbolPosition,
                      symbolSpacing,
                  })
                : {})}
            {...restProps}
        />
    );
};

export const CurrencyInput = Field(CurrencyInputElement);
